import {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCrown, faEnvelope, faRotate} from "@fortawesome/free-solid-svg-icons";
import {ProjectApi} from "../../api/authority/generated/index";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import {useIsAdministrator} from "../../components/auth/rolesAndPermissions.js";
import Modal from "../../components/Modal.jsx";
import ActionButton from "../../components/ActionButton.jsx";
import YesNo from "../../components/YesNo.jsx";
import Button from "../../components/Button.jsx";
import FormError from "../../components/form/FormError.jsx";
import toast from "react-hot-toast";
import Badge from "../../components/Badge.jsx";
import {formatDateTimeLabel} from "../admin/ReportingLab.jsx";

export default function ProjectPendingInvitations({project, refreshKey = 0}) {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const isAdministrator = useIsAdministrator();
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [deleteInvite, setDeleteInvite] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [resendingInvitationId, setResendingInvitationId] = useState(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);

        new ProjectApi(getAuthorityConfiguration())
            .projectInvites({projectId: project.id})
            .then((result) => {
                if (!active) return;
                setInvitations(Array.isArray(result) ? result : []);
            })
            .catch((requestError) => {
                if (!active) return;
                setError(requestError);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => { active = false; };
    }, [getAuthorityConfiguration, project.id, refreshKey, reloadKey]);

    const removeInvitation = async (invitation) => {
        setLoading(true);
        try {
            await new ProjectApi(getAuthorityConfiguration()).deleteProjectInvite({
                projectId: project.id,
                inviteId: invitation.id,
            });
            setInvitations((current) => current.filter(({id}) => id !== invitation.id));
            toast.success(t('project.invitation-deleted'));
        } catch (requestError) {
            const message = await et(requestError);
            if (message) toast.error(message);
        } finally {
            setDeleteInvite(null);
            setLoading(false);
        }
    };

    const resendInvitation = async (invitation) => {
        setResendingInvitationId(invitation.id);
        try {
            await new ProjectApi(getAuthorityConfiguration()).resendProjectInvite({
                projectId: project.id,
                inviteId: invitation.id,
            });
            toast.success(t('project.invitation-resent'));
        } catch (requestError) {
            const message = await et(requestError);
            if (message) toast.error(message);
        } finally {
            setResendingInvitationId(null);
        }
    };

    if (!loading && !error && invitations.length === 0) {
        return null;
    }

    if (error) {
        return <FormError error={error}/>;
    }

    if (loading && invitations.length === 0) {
        return null;
    }

    return <>
        <section className="app-surface mb-4 flex flex-col gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 dark:border-violet-500/30 dark:bg-violet-500/10 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                    <FontAwesomeIcon icon={faEnvelope}/>
                </span>
                <div>
                    <div className="font-semibold text-slate-900 dark:text-slate-100">
                        {t('project.pending-invitations', {count: invitations.length})}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                        {t('project.pending-invitations-description')}
                    </div>
                </div>
            </div>
            <Button variant="outline-black" onClick={() => setModalVisible(true)}>
                {t('project.show-invitations')}
            </Button>
        </section>

        <Modal
            visible={modalVisible}
            setVisible={setModalVisible}
            loading={loading}
            title={<>{t('project.pending-invitations-modal-title')} <span className="ml-1 text-sm font-semibold text-violet-600 dark:text-violet-400">({invitations.length})</span></>}
            className="max-w-2xl"
        >
            <div className="mb-3 flex justify-end">
                <Button
                    variant="outline-primary"
                    size="small"
                    className="h-9 !py-0 !shadow-none"
                    onClick={() => setReloadKey((current) => current + 1)}
                >
                    <FontAwesomeIcon icon={faRotate} className="mr-2"/>
                    {t('project.refresh-invitations')}
                </Button>
            </div>
            <div className="space-y-2">
                {invitations.map((invitation) => <article key={invitation.id} className="app-surface p-3 overflow-hidden rounded-xl border border-slate-200 bg-white transition-colors hover:border-violet-200 hover:bg-violet-50/30 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-violet-500/40 dark:hover:bg-violet-500/5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-sm text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                                <FontAwesomeIcon icon={faEnvelope}/>
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="min-w-0 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{invitation.email}</div>
                                    {invitation.isOwner
                                        ? <Badge color="violet"><FontAwesomeIcon icon={faCrown} className="mr-2"/>{t('project.owner')}</Badge>
                                        : <Badge color="violet">{invitation.role?.name ?? t('project.no-role')}</Badge>}
                                </div>
                                {invitation.created && <div className="mt-0 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                                    {formatDateTimeLabel(invitation.created, false)}
                                </div>}
                            </div>
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                            <Button
                                variant="outline-primary"
                                size="small"
                                className="h-9 !py-0 !shadow-none"
                                disabled={resendingInvitationId === invitation.id}
                                onClick={() => resendInvitation(invitation)}
                            >
                                {resendingInvitationId === invitation.id
                                    ? t('project.resending-invitation')
                                    : t('project.resend-invitation')}
                            </Button>
                            <ActionButton
                                icon="trash"
                                variant="primary-red"
                                size={36}
                                title={t('project.delete-invitation')}
                                onClick={() => setDeleteInvite(invitation)}
                            />
                        </div>
                    </div>
                    {isAdministrator && <div className="mt-2 -mb-1 text-right font-mono text-[9px] leading-3 text-violet-600 dark:text-violet-400">{invitation.id}</div>}
                </article>)}
            </div>
        </Modal>

        <YesNo
            title={t('confirmation')}
            question={t('project.really-delete-invitation-question', {email: deleteInvite?.email})}
            visible={deleteInvite !== null}
            setVisible={(visible) => { if (!visible) setDeleteInvite(null); }}
            onYes={() => removeInvitation(deleteInvite)}
            onNo={() => setDeleteInvite(null)}
        />
    </>;
}
